<script setup lang="ts">
/**
 * The EU taxonomy figures.
 *
 * Three rows, in the order the regulation asks for them, with the amounts as
 * entered and the shares worked out from them. The shares are derived because
 * a statement reporting 40% eligible next to amounts that come to 38% is one
 * nobody can tell which half of to believe, and both halves are audited.
 *
 * A row where aligned exceeds eligible is marked on the page rather than
 * silently corrected. Aligned is a subset of eligible, so it cannot happen;
 * capping it would hide the error instead of raising it, and this is a figure
 * somebody has to go and fix rather than one the builder should quietly
 * rewrite.
 */
import { useI18n } from 'vue-i18n'

import { computed } from 'vue'

import type { TaxonomySection } from '@/domain/section'
import { sharesOf } from '@/domain/taxonomy'

const { t } = useI18n()

const props = defineProps<{ section: TaxonomySection }>()

const shares = computed(() => sharesOf(props.section.kpis))

/** A share as a percentage, or nothing where it cannot be worked out. */
function percent(value: number | null): string {
  if (value === null) return ''
  return new Intl.NumberFormat('fr-FR', {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(value)
}
</script>

<template>
  <figure class="taxonomy">
    <p v-if="section.unit" class="page-unit">{{ section.unit }}</p>

    <table>
      <thead>
        <tr>
          <th>{{ t('inspector.taxonomy.kpi') }}</th>
          <th class="is-numeric">{{ t('inspector.taxonomy.total') }}</th>
          <th class="is-numeric">{{ t('inspector.taxonomy.eligible') }}</th>
          <th class="is-numeric">{{ t('inspector.taxonomy.eligibleShare') }}</th>
          <th class="is-numeric">{{ t('inspector.taxonomy.aligned') }}</th>
          <th class="is-numeric">{{ t('inspector.taxonomy.alignedShare') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="share in shares" :key="share.id">
          <td class="is-strong">{{ t(`inspector.taxonomy.kpis.${share.id}`) }}</td>
          <td class="is-numeric">{{ section.kpis.find((k) => k.id === share.id)?.total }}</td>
          <td class="is-numeric">{{ section.kpis.find((k) => k.id === share.id)?.eligible }}</td>
          <td class="is-numeric">{{ percent(share.eligibleShare) }}</td>
          <td class="is-numeric" :class="{ taxonomy__impossible: share.impossible }">
            {{ section.kpis.find((k) => k.id === share.id)?.aligned }}
          </td>
          <td class="is-numeric" :class="{ taxonomy__impossible: share.impossible }">
            {{ percent(share.alignedShare) }}
          </td>
        </tr>
      </tbody>
    </table>

    <p v-if="shares.some((share) => share.impossible)" class="taxonomy__warning">
      {{ t('inspector.taxonomy.impossible') }}
    </p>

    <figcaption v-if="section.caption" class="page-caption">{{ section.caption }}</figcaption>
  </figure>
</template>

<style scoped>
.taxonomy {
  margin: 0;
}

/* Marked, not corrected. A figure somebody has to go and fix. */
.taxonomy__impossible {
  color: #b3261e;
  font-weight: 600;
}

.taxonomy__warning {
  margin: 1.5mm 0 0;
  color: #b3261e;
  font-size: 8.5pt;
  line-height: 1.5;
  text-align: left;
}

@media print {
  .taxonomy__impossible,
  .taxonomy__warning {
    print-color-adjust: exact;
    -webkit-print-color-adjust: exact;
  }
}
</style>
